import { useCallback, useState } from 'react'
import type { JSX } from 'react'

import type { PriceExplanation } from '@lensmira/domain'
import { formatDisplayDate } from '@lensmira/i18n'

import { Ltr, useTranslation } from '../i18n.js'
import { formatMoneyForDisplay, parseMinorUnits } from '../money.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { sentence } from '../sentence.js'
import { saleTypeWord } from '../status-labels.js'
import { PriceExplanationList } from './price-explanation.js'

/**
 * One quote, and the two things that can happen to it. §6.6 `DSP-001`.
 *
 * **A quote is an order in `quoted`, so this screen is not where one is found.** `GET /orders`
 * already lists them and `OrderList` already shows them, which is why there is no quote list here
 * and no list endpoint on the server: a second list of the same rows is a second place for the two
 * to disagree. `OrderList` opens one here when its status is `quoted`.
 *
 * **What is frozen is the price, and that is the whole point of the record.** Appendix V.5: a
 * superseded price book does not change what was quoted, and `priceBookVersionId` on the row is
 * what makes that true. So every amount here is shown exactly as the server stored it at issue and
 * nothing on this screen recomputes one. `PRC-011` `MUST` puts "a visible price explanation on
 * every quote", which is `PriceExplanationList`, shared with `OrderDetail` rather than copied.
 *
 * **`lapsed` is not the same thing as `expired`.** `expired` is a status somebody or something
 * wrote; `lapsed` is the server comparing the stored expiry against its own clock on this read.
 * A quote reading `quoted` with `lapsed` set is the ordinary case, because nothing sweeps expiries:
 * `checkQuoteConversion` refuses the conversion with a 409, so this screen says the quote has run
 * out and offers to record that rather than offering a conversion that cannot succeed. The server
 * is the gate; this is the courtesy.
 */

export interface QuoteLineData {
  readonly id: string
  readonly description: string
  readonly quantity: number
  readonly grossMinor: string
  /** `DSP-003`: which pair, on a two-pair sale. Absent on a line that is not a lens. */
  readonly pairNumber?: number
  readonly eye?: string
  readonly priceExplanation?: readonly PriceExplanation[]
}

export interface QuoteData {
  readonly id: string
  readonly customerId: string
  /** Appendix E, as the server froze it. Not looked up from the registry, which can change. */
  readonly saleType: string
  /** Appendix C.1: `quoted`, `confirmed` or `expired`. */
  readonly status: string
  /** The server's verdict about its own clock on this read, never a status. */
  readonly lapsed: boolean
  readonly issuedAt: string
  readonly expiresAt: string
  /** ISO 4217, shared by every amount here. */
  readonly currency: string
  readonly subtotalMinor: string
  readonly orderDiscountMinor: string
  readonly taxMinor: string
  readonly totalMinor: string
  /** Set once converted. The order it became, which is where payment is taken. */
  readonly convertedToOrderId?: string
  readonly lines: readonly QuoteLineData[]
  readonly version: number
}

export interface QuoteProps {
  readonly quote: QuoteData
  /** `BR-01`: the branch's own zone. A tablet in a shop can have any clock at all. */
  readonly timezone: string
  readonly customerName?: string
  /** Called once the server has taken a conversion or an expiry, so the caller can re-read. */
  readonly onSettled?: () => void
  /** Open the order a converted quote became, where the money is taken. */
  readonly onOpenOrder?: (orderId: string) => void
}

const amount = (minor: string, currency: string): string =>
  formatMoneyForDisplay(parseMinorUnits(minor), currency)

export function Quote({
  quote,
  timezone,
  customerName,
  onSettled,
  onOpenOrder,
}: QuoteProps): JSX.Element {
  const { t, locale } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [writeId, setWriteId] = useState<string | null>(null)

  useWriteSettled(snapshot, writeId, () => {
    setWriteId(null)
    onSettled?.()
  })

  const day = (iso: string): string => {
    const instant = Date.parse(iso)
    return Number.isFinite(instant) ? formatDisplayDate(instant, timezone, locale) : iso
  }

  const act = useCallback(
    async (path: 'conversion' | 'expiry', label: string) => {
      const write = (await enqueue({
        method: 'POST',
        path: `/quotes/${quote.id}/${path}`,
        label,
        // `ARC-007`: the version this screen read. Another terminal converting the same quote in
        // between is a conflict with the current state, not a second order for one sale.
        body: { expectedVersion: quote.version },
      })) as { id: string }
      setWriteId(write.id)
    },
    [enqueue, quote.id, quote.version],
  )

  const line = statusLine(t, snapshot, writeId, t('queue.subject.quote'))
  // Convertible only while it is genuinely open. The server decides, and this spares a shopkeeper
  // a refusal it already knows the answer to.
  const open = quote.status === 'quoted' && !quote.lapsed

  return (
    <section className="lm-screen" aria-labelledby="quote-heading">
      <h1 id="quote-heading" className="lm-screen__heading">
        {customerName === undefined
          ? t('quote.heading')
          : t('quote.headingFor', { name: customerName })}
      </h1>

      <p className="lm-hint">
        {sentence(t, 'quote.issuedExpires', {
          saleType: saleTypeWord(t, quote.saleType),
          issued: <Ltr>{day(quote.issuedAt)}</Ltr>,
          expires: <Ltr>{day(quote.expiresAt)}</Ltr>,
        })}
      </p>

      {/* Three states, each its own whole sentence rather than one with a word swapped, because
          none of them keeps its clause order in every language. */}
      {quote.status === 'confirmed' ? (
        <p className="lm-hint" role="status">
          {t('quote.converted')}
        </p>
      ) : quote.status === 'expired' ? (
        <p className="lm-hint" role="status">
          {t('quote.expired')}
        </p>
      ) : quote.lapsed ? (
        <p className="lm-hint" role="alert">
          {/* Not "expired": nothing has written that status yet, and saying it had would be telling
              the shopkeeper the record says something it does not. */}
          {t('quote.lapsed')}
        </p>
      ) : null}

      <table className="lm-line-table">
        <caption className="lm-rx-grid__caption">{t('quote.linesCaption')}</caption>
        <thead>
          <tr>
            <th scope="col">{t('orderDetail.col.description')}</th>
            <th scope="col">{t('common.quantity')}</th>
            <th scope="col">{t('common.total')}</th>
          </tr>
        </thead>
        <tbody>
          {quote.lines.map((row) => (
            <tr key={row.id}>
              <th scope="row" className="lm-line-table__description">
                {row.description}
                {row.pairNumber === undefined ? null : (
                  <span className="lm-line-table__meta">
                    {t('quote.pair', { pair: row.pairNumber })}
                  </span>
                )}
                <PriceExplanationList
                  t={t}
                  lineId={row.id}
                  description={row.description}
                  entries={row.priceExplanation ?? []}
                  currency={quote.currency}
                />
              </th>
              <td className="lm-line-table__num" dir="ltr">
                {row.quantity}
              </td>
              <td className="lm-line-table__num" dir="ltr">
                {amount(row.grossMinor, quote.currency)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <dl className="lm-order-card__amount">
        <dt>{t('quote.subtotal')}</dt>
        <dd dir="ltr">{amount(quote.subtotalMinor, quote.currency)}</dd>
        <dt>{t('quote.orderDiscount')}</dt>
        {/* `MR-05` apportions this back to the lines, so it is shown here as what was agreed and
            never used to adjust a line total on screen. */}
        <dd dir="ltr">{amount(quote.orderDiscountMinor, quote.currency)}</dd>
        <dt>{t('orderDetail.col.tax')}</dt>
        <dd dir="ltr">{amount(quote.taxMinor, quote.currency)}</dd>
        <dt>{t('common.total')}</dt>
        <dd dir="ltr">{amount(quote.totalMinor, quote.currency)}</dd>
      </dl>

      {line === null ? null : (
        <p className="lm-queue-state" role="status">
          {line}
        </p>
      )}

      {quote.convertedToOrderId !== undefined && onOpenOrder ? (
        <button
          type="button"
          className="lm-button lm-button--primary"
          onClick={() => {
            onOpenOrder(quote.convertedToOrderId ?? '')
          }}
        >
          {t('quote.openOrder')}
        </button>
      ) : null}

      {open ? (
        <button
          type="button"
          className="lm-button lm-button--primary"
          onClick={() => void act('conversion', t('quote.queue.convertLabel'))}
        >
          {t('quote.convert')}
        </button>
      ) : null}

      {quote.status === 'quoted' ? (
        <button
          type="button"
          className="lm-button lm-button--secondary"
          onClick={() => void act('expiry', t('quote.queue.expireLabel'))}
        >
          {/* Offered on a lapsed quote too, and that is the case it is for: the date has passed and
              nothing has written the status, so this is how a shopkeeper records what happened. */}
          {t('quote.recordExpiry')}
        </button>
      ) : null}
    </section>
  )
}
